import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectBatchProgress,
  resetBatchProgress,
} from '../../../features/patches/patchesSlice';
import { BatchStatusIndicator } from './index';
import { useFeatureFlag } from '../../../services/optimizely/optimizelyHooks';
import { FeatureFlags } from '../../../constants/FeatureFlags';
import { getPatch } from '../../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import { PatchTypeSelector } from '../PatchTypeSelector';
// Note: PendingChangesView import removed as it's no longer used in this component
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Divider,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { PatchType, Store } from '../../../types';

// Styled component for loading spinner
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingSpinner = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  width: '20px',
  height: '20px',
  border: '2px solid #f3f3f3',
  borderTop: '2px solid #3498db',
  borderRadius: '50%',
  animation: `${spin} 2s linear infinite`,
}));

type PatchListProps = {
  stores: Store[];
  storeNumbers: string[];
  patchType: PatchType;
  handlePatchTypeChange: (patchType: PatchType) => void;
  getPatchIdsForType?: (patchType: PatchType) => string[];
  getPatchDataForType?: (patchType: PatchType) => any[];
};

const PatchList: React.FC<PatchListProps> = ({
  stores,
  storeNumbers,
  patchType,
  handlePatchTypeChange,
  getPatchIdsForType,
  getPatchDataForType,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showBatchToast, setShowBatchToast] = useState(false);
  const [patchCache, setPatchCache] = useState<Record<string, any>>({});
  const [loadingPatches, setLoadingPatches] = useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const errorTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const batchProgress = useSelector(selectBatchProgress);
  const dispatch = useDispatch();
  const { isEnabled: patchEditEnabled } = useFeatureFlag(FeatureFlags.PATCH_EDIT_ENABLED);

  // Error message cleanup function
  const clearErrorMessage = () => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setErrorMessage(null);
  };

  // Handle patch data fetching and caching with proper error handling
  const handleLoadPatchDetails = useCallback(async (patchId: string) => {
    // Validate input
    if (!patchId || typeof patchId !== 'string') {
      setErrorMessage('Invalid patch ID provided');
      return;
    }

    // Don't fetch if already cached or currently loading
    if (patchCache[patchId] || loadingPatches.has(patchId)) {
      return;
    }

    setLoadingPatches(prev => new Set(prev).add(patchId));
    
    try {
      const patchData = await getPatch(patchId);
      
      // Validate response data
      if (!patchData || typeof patchData !== 'object') {
        throw new Error('Invalid patch data received');
      }

      setPatchCache(prev => ({
        ...prev,
        [patchId]: patchData
      }));
    } catch (error) {
      console.error(`Failed to load patch details for ${patchId}:`, error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      setErrorMessage(`Failed to load patch details: ${errorMsg}`);
      
      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      // Clear error message after 5 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setErrorMessage(null);
        errorTimeoutRef.current = null;
      }, 5000);
    } finally {
      setLoadingPatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(patchId);
        return newSet;
      });
    }
  }, [patchCache, loadingPatches]);

  // Removed useExtractedData state, not needed
  const [filteredPatchData, setFilteredPatchData] = useState<any[]>([]);
  
  // Memoize operation count calculation to prevent unnecessary recalculations
  const getOperationCount = useMemo(() => (patch: any) => {
    if (patchCache[patch.patchId]?.patchOperations?.edges) {
      return patchCache[patch.patchId].patchOperations.edges.length;
    }
    return null;
  }, [patchCache]);

  // Memoize filtered patches to prevent unnecessary re-renders
  const memoizedPatches = useMemo(() => {
    return filteredPatchData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredPatchData, page, rowsPerPage]);
  const [patchFranchiseCodes] = useState<Record<string, string>>({});
  
  // Auto-reload patch list on patchType or storeNumbers change
  useEffect(() => {
    if (getPatchDataForType) {
      let patchesForType = getPatchDataForType(patchType);

      // Prefer short patch name (e.g., 000000_SetProdPrices) if available
      const getShortPatchName = (patch: any) => {
        if (patch.name && /^\d+_/.test(patch.name)) return patch.name;
        if (patch.patchName && /^\d+_/.test(patch.patchName)) return patch.patchName;
        return patch.name || patch.patchName || '';
      };

      // Sort patches to prefer short name first
      patchesForType = patchesForType.slice().sort((a, b) => {
        const aShort = /^\d+_/.test(getShortPatchName(a));
        const bShort = /^\d+_/.test(getShortPatchName(b));
        if (aShort && !bShort) return -1;
        if (!aShort && bShort) return 1;
        return getShortPatchName(a).localeCompare(getShortPatchName(b));
      });

  setFilteredPatchData(patchesForType);
    } else {
  setFilteredPatchData([]);
    }
  }, [getPatchDataForType, patchType, storeNumbers]);

  // Use filtered patch data as the patches array
  const patches = filteredPatchData;

  // Reset batch progress when component unmounts or filters change
  useEffect(() => {
    return () => {
      dispatch(resetBatchProgress());
      // Clean up any pending error timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      // Clear any pending async operations
      setLoadingPatches(new Set());
      setErrorMessage(null);
    };
  }, [dispatch, patchType, storeNumbers]);

  // Clean up cache when filters change to prevent memory leaks
  useEffect(() => {
    setPatchCache({});
    setLoadingPatches(new Set());
  }, [patchType, storeNumbers]);

  // Show toast when batch progress is complete
  useEffect(() => {
    try {
      if (batchProgress && typeof batchProgress === 'object' && 
          batchProgress.isComplete && batchProgress.total > 0) {
        setShowBatchToast(true);
      }
    } catch (error) {
      console.error('Error processing batch progress:', error);
      setErrorMessage('Error processing batch operation status');
    }
  }, [batchProgress?.isComplete, batchProgress?.total]);

  // Handle closing the toast
  const handleCloseToast = () => {
    setShowBatchToast(false);
  };

  // No menu channel or fetch patches handler needed

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Always show the patch list, auto-loads on patchType/storeNumbers change


  const patchCount = patches.length;

  // No patches found
  if (patchCount === 0) {
    return (
      <Paper sx={{ width: '100%', mb: 2, p: 2 }} data-testid='noPatchesFoundPaper'>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Patch Type:
            </Typography>
            <PatchTypeSelector
              value={patchType}
              onChange={handlePatchTypeChange}
              variant='select'
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Alert severity='info'>
          No patches found for the selected stores and patch type.
        </Alert>
      </Paper>
    );
  }

  // Render the patches in a table with batch status indicator
  // If using new pendingOps structure, pass enriched ops for display
  // TODO: Replace this with the correct pendingOps from useBulkUpdate if available in this scope
  // For now, fallback to old logic if not refactored yet
  return (
    <Box>
      <Paper sx={{ width: '100%', mb: 2 }} data-testid='patchListPaper'>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Patch Type:
              </Typography>
              <PatchTypeSelector
                value={patchType}
                onChange={handlePatchTypeChange}
                variant='select'
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size='medium'>
            <TableHead>
              <TableRow>
                <TableCell>Patch Name</TableCell>
                <TableCell>Patch ID</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>FZ Code</TableCell>
                <TableCell>Total Operations</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memoizedPatches.map((patch: any) => {
                const totalOperations = getOperationCount(patch);
                const isLoading = loadingPatches.has(patch.patchId);
                let store = stores.find((s) =>
                  s.assignedMenuDefinitions?.some((menuDef) =>
                    menuDef.patches?.some((p) => p.patch.patchId === patch.patchId)
                  )
                );
                if (!store && patch.storeNumber) {
                  store = stores.find((s) => s.storeNumber === patch.storeNumber);
                }
                if (!store && patch.storeNumbers && patch.storeNumbers.length > 0) {
                  store = stores.find((s) => s.storeNumber === patch.storeNumbers[0]);
                }
                let extractedStoreNumber: string | null = null;
                if (patch.name) {
                  const patchNameMatch = patch.name.match(/^(\d+)_/);
                  if (patchNameMatch && patchNameMatch[1]) {
                    extractedStoreNumber = patchNameMatch[1];
                  }
                }
                const storeNumber =
                  store?.storeNumber ||
                  patch.storeNumber ||
                  extractedStoreNumber ||
                  (patch.storeNumbers && patch.storeNumbers.length > 0 ? patch.storeNumbers[0] : 'N/A');
                const franchiseCode = patchFranchiseCodes[patch.patchId] || 'N/A';
                const getShortPatchName = (patch: any) => {
                  if (patch.name && /^\d+_/.test(patch.name)) return patch.name;
                  if (patch.patchName && /^\d+_/.test(patch.patchName)) return patch.patchName;
                  return patch.name || patch.patchName || `Patch ${patch.patchId}`;
                };
                return (
                  <TableRow key={patch.patchId}>
                    <TableCell>{getShortPatchName(patch)}</TableCell>
                    <TableCell>{patch.patchId}</TableCell>
                    <TableCell>
                      {storeNumber !== 'N/A' ? <Chip label={storeNumber} size='small' variant='outlined' /> : 'N/A'}
                    </TableCell>
                    <TableCell>{franchiseCode}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ minWidth: '40px' }}>
                          {isLoading ? (
                            <LoadingSpinner 
                              component="span" 
                            />
                          ) : (
                            totalOperations !== null ? totalOperations.toLocaleString() : '—'
                          )}
                        </Typography>
                        {patchEditEnabled && (
                          <Tooltip title={isLoading ? "Loading patch details..." : "Edit patch operations"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleLoadPatchDetails(patch.patchId)}
                                disabled={isLoading}
                                sx={{ 
                                  opacity: isLoading ? 0.5 : 1,
                                  '&:hover': {
                                    backgroundColor: 'action.hover'
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={patches.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Batch Status Toast */}
      <Snackbar
        open={showBatchToast}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ minWidth: '300px' }}>
          <BatchStatusIndicator
            batchProgress={batchProgress}
            storeCount={storeNumbers.length}
            isLoading={false}
            isFetching={false}
            error={undefined}
          />
        </Box>
      </Snackbar>
      
      {/* Error Toast */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={clearErrorMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={clearErrorMessage}
          severity='error'
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatchList;
